const STORAGE_KEY = 'bayaelaScratchWin';
const AUTO_REVEAL_THRESHOLD = 0.55;
const OPEN_DELAY_MS = 1200;

function readState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeState(patch) {
  try {
    const next = { ...readState(), ...patch };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
  } catch {
    return { ...readState(), ...patch };
  }
}

class ScratchWinPopup {
  constructor(root) {
    this.root = root;
    this.panels = {
      scratch: root.querySelector('[data-step-panel="scratch"]'),
      email: root.querySelector('[data-step-panel="email"]'),
      success: root.querySelector('[data-step-panel="success"]'),
    };
    this.canvas = root.querySelector('[data-scratch-canvas]');
    this.ctx = this.canvas ? this.canvas.getContext('2d', { willReadFrequently: true }) : null;
    this.announcer = root.querySelector('[data-scratch-announcer]');
    this.revealButton = root.querySelector('[data-scratch-reveal]');
    this.copyButton = root.querySelector('[data-copy-code]');
    this.copyStatus = root.querySelector('[data-copy-status]');
    this.copyButtonDefaultLabel = this.copyButton?.textContent || '';
    this.copyResetTimer = null;

    this.codes = {
      10: root.dataset.code10 || '',
      12: root.dataset.code12 || '',
    };

    this.scratching = false;
    this.canvasReady = false;
    this.revealed = false;
    this.lastPoint = null;
    this.lastCheckAt = 0;

    this.#bindEvents();
    this.#restoreOrSchedule();
  }

  #bindEvents() {
    this.root.addEventListener('dialog:open', () => this.#onDialogOpen());
    this.root.addEventListener('dialog:close', () => this.#onDialogClose());

    if (this.canvas) {
      this.canvas.addEventListener('pointerdown', (event) => this.#startScratch(event));
      this.canvas.addEventListener('pointermove', (event) => this.#continueScratch(event));
      this.canvas.addEventListener('pointerup', () => this.#endScratch());
      this.canvas.addEventListener('pointercancel', () => this.#endScratch());
      this.canvas.addEventListener('pointerleave', () => this.#endScratch());
    }

    this.revealButton?.addEventListener('click', () => this.#completeReveal());

    this.copyButton?.addEventListener('click', () => this.#copyCode());

    // A tab that was backgrounded when the popup auto-opened (e.g. opened in
    // a new tab while the visitor kept browsing elsewhere) can hand the
    // canvas a zero-size layout rect, since hidden tabs skip layout. Retry
    // once the tab actually becomes visible, so the card isn't left blank.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !this.canvasReady) {
        this.#onDialogOpen();
      }
    });
  }

  #restoreOrSchedule() {
    const stateMarker = this.root.querySelector('[data-scratch-state]');
    const postedSuccessfully = stateMarker?.dataset.posted === 'true';
    const hadFormError = stateMarker?.dataset.error === 'true';

    const state = readState();

    if (state.step === 'redeemed' && !postedSuccessfully) return;

    if (postedSuccessfully) {
      const prize = state.prize || 10;
      writeState({ prize, step: 'redeemed' });
      this.#setPrize(prize);
      this.#showPanel('success');
      this.#fillDiscountCode(prize);
      this.root.showDialog?.();
      return;
    }

    if (hadFormError) {
      const prize = state.prize || 10;
      this.#setPrize(prize);
      this.#showPanel('email');
      this.root.showDialog?.();
      return;
    }

    if (state.step === 'closed' || state.step === 'redeemed') return;

    if (state.step === 'revealed') {
      this.revealed = true;
      this.#setPrize(state.prize || 10);
      this.#showPanel('email');
      return;
    }

    // 'pending' means the popup was already auto-opened once before (on an
    // earlier page) and the visitor hasn't closed/revealed/redeemed it since
    // — without this, every new page load would fall through to the
    // auto-show branch below and reopen the popup again and again as the
    // visitor browses.
    if (state.step === 'pending') return;

    const prize = state.prize || (Math.random() < 0.5 ? 10 : 12);
    writeState({ prize, step: 'pending' });
    this.#setPrize(prize);

    window.setTimeout(() => {
      if (readState().step === 'closed') return;
      this.root.showDialog?.();
    }, OPEN_DELAY_MS);
  }

  #onDialogOpen() {
    if (!this.canvasReady && this.panels.scratch && !this.panels.scratch.hidden) {
      this.#initCanvas();
    }
  }

  #onDialogClose() {
    const state = readState();
    if (state.step !== 'redeemed') {
      writeState({ step: 'closed' });
    }
  }

  #setPrize(prize) {
    this.prize = prize;
    this.root.querySelectorAll('[data-prize-percent]').forEach((el) => {
      el.textContent = `${prize}%`;
    });
    this.root.querySelectorAll('[data-prize-percent-text]').forEach((el) => {
      el.textContent = `${prize}%`;
    });

    // Tags the customer record with which prize they won (e.g. "scratch-win-12"),
    // so a Shopify Flow automation can send the matching code by email — this
    // popup itself has no way to send email, only to reveal/copy the code.
    const tagInput = this.root.querySelector('[data-prize-tag]');
    if (tagInput) tagInput.value = `scratch-win-${prize}`;
  }

  #fillDiscountCode(prize) {
    const codeEl = this.root.querySelector('[data-discount-code]');
    if (!codeEl) return;
    // Never blank out the code that's already sitting in the markup — only
    // overwrite it once we actually have a real code to replace it with.
    const code = this.codes[prize] || this.codes[10];
    if (code) codeEl.textContent = code;
  }

  #showPanel(name) {
    for (const [key, panel] of Object.entries(this.panels)) {
      if (!panel) continue;
      panel.hidden = key !== name;
    }

    const sharedHeading = this.root.querySelector('[data-dialog-heading]');
    const activeHeading = this.panels[name]?.querySelector('h2');
    if (sharedHeading && activeHeading) {
      sharedHeading.textContent = activeHeading.textContent.trim();
    }
  }

  #initCanvas() {
    if (!this.canvas || !this.ctx) return;
    const rect = this.canvas.getBoundingClientRect();

    // A hidden/backgrounded tab skips layout, so this can read back 0x0 —
    // leave canvasReady false so #onDialogOpen (or the visibilitychange
    // listener above) tries again instead of permanently drawing nothing.
    if (rect.width <= 0 || rect.height <= 0) return;

    const dpr = Math.min(Math.max(window.devicePixelRatio || 1, 1), 2);

    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.scale(dpr, dpr);
    this.displayWidth = rect.width;
    this.displayHeight = rect.height;

    const gradient = this.ctx.createLinearGradient(0, 0, rect.width, rect.height);
    gradient.addColorStop(0, '#e9dfd2');
    gradient.addColorStop(0.5, '#f4ede2');
    gradient.addColorStop(1, '#e2d5c3');
    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, rect.width, rect.height);

    this.ctx.font = '600 15px var(--font-body--family, sans-serif)';
    this.ctx.fillStyle = 'rgba(90, 74, 58, 0.55)';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('גרדי כאן ✨', rect.width / 2, rect.height / 2);

    this.ctx.globalCompositeOperation = 'destination-out';
    this.canvasReady = true;
  }

  #pointFromEvent(event) {
    const rect = this.canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  #startScratch(event) {
    if (this.revealed || !this.canvasReady) return;
    this.scratching = true;
    this.canvas.setPointerCapture?.(event.pointerId);
    this.lastPoint = this.#pointFromEvent(event);
    this.#scratchAt(this.lastPoint);
  }

  #continueScratch(event) {
    if (!this.scratching || this.revealed) return;
    const point = this.#pointFromEvent(event);
    this.#scratchLine(this.lastPoint, point);
    this.lastPoint = point;
    this.#scheduleScratchCheck();
  }

  #endScratch() {
    this.scratching = false;
    this.#checkScratchedAmount();
  }

  #scratchAt(point) {
    this.ctx.beginPath();
    this.ctx.arc(point.x, point.y, 22, 0, Math.PI * 2);
    this.ctx.fill();
  }

  #scratchLine(from, to) {
    this.ctx.lineWidth = 44;
    this.ctx.lineCap = 'round';
    this.ctx.beginPath();
    this.ctx.moveTo(from.x, from.y);
    this.ctx.lineTo(to.x, to.y);
    this.ctx.stroke();
    this.#scratchAt(to);
  }

  #scheduleScratchCheck() {
    const now = performance.now();
    if (now - this.lastCheckAt < 180) return;
    this.lastCheckAt = now;
    this.#checkScratchedAmount();
  }

  #checkScratchedAmount() {
    if (!this.ctx || this.revealed || !this.displayWidth) return;

    const step = 4;
    const { data } = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);

    let transparent = 0;
    let sampled = 0;
    for (let i = 3; i < data.length; i += 4 * step) {
      sampled++;
      if (data[i] === 0) transparent++;
    }

    const ratio = sampled ? transparent / sampled : 0;
    if (ratio >= AUTO_REVEAL_THRESHOLD) {
      this.#completeReveal();
    }
  }

  #completeReveal() {
    if (this.revealed) return;
    this.revealed = true;

    if (this.canvas) {
      this.canvas.style.transition = 'opacity 320ms ease';
      this.canvas.style.opacity = '0';
      window.setTimeout(() => {
        this.canvas.style.display = 'none';
      }, 320);
    }

    if (this.announcer) {
      this.announcer.textContent = `גילית הנחה של ${this.prize}%! מלאי אימייל כדי לקבל את הקוד.`;
    }

    writeState({ step: 'revealed' });

    window.setTimeout(() => {
      this.#showPanel('email');
      this.root.querySelector('[data-step-panel="email"] input[type="email"]')?.focus();
    }, 550);
  }

  async #copyCode() {
    const codeEl = this.root.querySelector('[data-discount-code]');
    const code = codeEl?.textContent?.trim();
    if (!code) return;

    const succeeded = await this.#writeToClipboard(code);
    this.#showCopyFeedback(succeeded);
  }

  /**
   * Tries the modern async Clipboard API first (needs a secure context and
   * an active user gesture — both true here since this only ever runs from
   * a click handler). Falls back to the classic execCommand('copy') trick
   * via an offscreen textarea for browsers/contexts where the Clipboard API
   * is missing or its permission is denied, so the button still works.
   */
  async #writeToClipboard(text) {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // fall through to the legacy approach below
      }
    }
    return this.#legacyCopy(text);
  }

  #legacyCopy(text) {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }

  /**
   * The copy button's own label is the feedback — visible to every sighted
   * visitor, not just an aria-live region only screen readers would hear.
   */
  #showCopyFeedback(succeeded) {
    if (this.copyStatus) {
      this.copyStatus.textContent = succeeded
        ? 'הקוד הועתק!'
        : 'לא ניתן להעתיק אוטומטית — יש לסמן ולהעתיק את הקוד ידנית.';
    }

    if (!this.copyButton) return;

    this.copyButton.textContent = succeeded ? 'הועתק ✓' : 'לא הצליח להעתיק';
    window.clearTimeout(this.copyResetTimer);
    this.copyResetTimer = window.setTimeout(() => {
      this.copyButton.textContent = this.copyButtonDefaultLabel;
    }, 2200);
  }
}

function init() {
  const root = document.querySelector('#ScratchWinPopup');
  if (!root) return;
  new ScratchWinPopup(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
